<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">編輯座位</h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-secondary" @click="handleCancel">
          <i class="ri-close-line"></i>
          取消
        </button>
        <button type="button" class="btn btn-primary" :disabled="submitting" @click="handleSubmit">
          <span v-if="submitting">處理中...</span>
          <span v-else>更新</span>
        </button>
      </div>
    </div>

    <div class="page-container">
    <div class="create-table-container">

    <div class="form-container">
      <form @submit.prevent="handleSubmit">
        <div class="form-section">
          <h3 class="section-title">座位資訊</h3>

          <div class="form-row">
            <div class="form-group full-width">
              <label class="required">門市</label>
              <StoreSelector
                v-model="storeId"
                :stores="stores"
                :loading="storesLoading"
                :clearable="false"
              />
              <p class="form-hint">可將此座位改掛到其他門市。同一門市內的座位編號不可重複。</p>
            </div>
          </div>
          
          <div class="form-row">
            <div class="form-group">
              <label for="tableNumber" class="required">座位編號</label>
              <input
                id="tableNumber"
                v-model="form.tableNumber"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': errors.tableNumber }"
                placeholder="例如：A1, B2"
                required
                maxlength="20"
              />
              <div v-if="errors.tableNumber" class="invalid-feedback">
                {{ errors.tableNumber }}
              </div>
            </div>

            <div class="form-group">
              <label for="tableType">座位類型</label>
              <select
                id="tableType"
                v-model="form.tableType"
                class="form-control"
              >
                <option value="">請選擇</option>
                <option
                  v-for="type in tableTypeOptions"
                  :key="type"
                  :value="type"
                >
                  {{ type }}
                </option>
              </select>
            </div>
          </div>

          <div class="form-row" v-if="form.tableType === '包廂'">
            <div class="form-group">
              <label for="roomCategory">包廂類別</label>
              <input
                id="roomCategory"
                v-model="form.roomCategory"
                type="text"
                list="room-category-options"
                class="form-control"
                :class="{ 'is-invalid': errors.roomCategory }"
                placeholder="例如：VIP包廂、景觀包廂"
                maxlength="50"
              />
              <datalist id="room-category-options">
                <option v-for="category in roomCategories" :key="category" :value="category" />
              </datalist>
              <div v-if="errors.roomCategory" class="invalid-feedback">
                {{ errors.roomCategory }}
              </div>
            </div>
            <div class="form-group">
              <label for="roomMinSpend">包廂低消（元）</label>
              <input
                id="roomMinSpend"
                v-model.number="form.roomMinSpend"
                type="number"
                class="form-control"
                :class="{ 'is-invalid': errors.roomMinSpend }"
                placeholder="例如：3000"
                min="0"
                step="1"
              />
              <div v-if="errors.roomMinSpend" class="invalid-feedback">
                {{ errors.roomMinSpend }}
              </div>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="minCapacity" class="required">容納人數下限</label>
              <input
                id="minCapacity"
                v-model.number="form.minCapacity"
                type="number"
                class="form-control"
                :class="{ 'is-invalid': errors.minCapacity }"
                min="1"
                required
              />
              <div v-if="errors.minCapacity" class="invalid-feedback">
                {{ errors.minCapacity }}
              </div>
            </div>
            <div class="form-group">
              <label for="maxCapacity" class="required">容納人數上限</label>
              <input
                id="maxCapacity"
                v-model.number="form.maxCapacity"
                type="number"
                class="form-control"
                :class="{ 'is-invalid': errors.maxCapacity }"
                min="1"
                required
              />
              <div v-if="errors.maxCapacity" class="invalid-feedback">
                {{ errors.maxCapacity }}
              </div>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="floor">樓層</label>
              <input
                id="floor"
                v-model="form.floor"
                type="text"
                class="form-control"
                placeholder="例如：1F, 2F"
                maxlength="20"
              />
            </div>

            <div class="form-group">
              <label for="area">區域</label>
              <input
                id="area"
                v-model="form.area"
                type="text"
                class="form-control"
                placeholder="例如：靠窗區、包廂區"
                maxlength="50"
              />
            </div>

            <div class="form-group">
              <label for="isActive">狀態</label>
              <select
                id="isActive"
                v-model="form.isActive"
                class="form-control"
              >
                <option :value="true">啟用</option>
                <option :value="false">停用</option>
              </select>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group full-width">
              <label for="features">特殊功能</label>
              <textarea
                id="features"
                v-model="form.features"
                class="form-control"
                rows="3"
                placeholder="例如：靠窗、有插座、可併桌"
              ></textarea>
            </div>
          </div>
        </div>

      </form>
    </div>
    </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { reservationService } from '@/api/modules/reservations.js';
import { storeService } from '@/api/modules/stores.js';
import StoreSelector from '@/components/StoreSelector.vue';
import { showSuccess, showError } from '@/utils/swal.js';

export default {
  name: 'EditTable',
  components: { StoreSelector },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const storeId = ref(typeof route.query.storeId === 'string' ? route.query.storeId : '');
    const stores = ref([]);
    const storesLoading = ref(false);
    const submitting = ref(false);
    const errors = ref({});
    const tableTypeOptions = ref(['2人桌', '4人桌', '6人桌', '8人桌', '包廂']);
    const roomCategories = ref([]);

    const form = ref({
      tableNumber: '',
      tableType: '',
      roomCategory: '',
      roomMinSpend: null,
      capacity: 4,
      minCapacity: 4,
      maxCapacity: 4,
      floor: '',
      area: '',
      features: '',
      isActive: true
    });

    const backToList = () => {
      router.push({
        path: '/client/tables',
        query: storeId.value ? { storeId: storeId.value } : {}
      });
    };

    const fetchStores = async () => {
      storesLoading.value = true;
      try {
        const response = await storeService.getStores({ page: 1, pageSize: 100 });
        stores.value = response.success && response.data?.items ? response.data.items : [];
      } catch (err) {
        console.error('Failed to fetch stores:', err);
        showError('載入門市失敗');
      } finally {
        storesLoading.value = false;
      }
    };

    const fetchTable = async () => {
      try {
        const queryStoreId = typeof route.query.storeId === 'string' ? route.query.storeId : '';
        if (!queryStoreId) {
          showError('缺少門市參數，無法查詢座位');
          backToList();
          return;
        }
        const response = await reservationService.getTable(queryStoreId, route.params.id);
        if (response.success && response.data) {
          const table = response.data;
          // 若資料中的 tableType 不在預設清單，動態加入以便正確顯示
          if (table.tableType && !tableTypeOptions.value.includes(table.tableType)) {
            tableTypeOptions.value.push(table.tableType);
          }
          if (table.roomCategory && !roomCategories.value.includes(table.roomCategory)) {
            roomCategories.value.push(table.roomCategory);
          }
          form.value = {
            tableNumber: table.tableNumber,
            tableType: table.tableType || '',
            roomCategory: table.roomCategory || '',
            roomMinSpend: table.roomMinSpend ?? null,
            capacity: table.capacity,
            minCapacity: table.minCapacity || table.capacity,
            maxCapacity: table.maxCapacity || table.capacity,
            floor: table.floor || '',
            area: table.area || '',
            features: table.features || '',
            isActive: table.isActive
          };
          storeId.value = table.storeId || queryStoreId;
        } else {
          throw new Error('查詢座位失敗');
        }
      } catch (err) {
        showError(err.response?.data?.message || err.message || '查詢座位失敗');
        backToList();
      }
    };

    const fetchRoomCategories = async () => {
      try {
        const response = await reservationService.getRoomCategories();
        if (response.success && response.data?.items) {
          roomCategories.value = response.data.items;
        }
      } catch (err) {
        console.error('Failed to fetch room categories:', err);
      }
    };

    const handleSubmit = async () => {
      if (!storeId.value) {
        showError('請選擇門市');
        return;
      }

      errors.value = {};
      submitting.value = true;

      try {
        const minCapacity = Number(form.value.minCapacity);
        const maxCapacity = Number(form.value.maxCapacity);
        if (!Number.isInteger(minCapacity) || minCapacity < 1) {
          showError('容納人數下限必須為大於 0 的整數');
          submitting.value = false;
          return;
        }
        if (!Number.isInteger(maxCapacity) || maxCapacity < 1) {
          showError('容納人數上限必須為大於 0 的整數');
          submitting.value = false;
          return;
        }
        if (minCapacity > maxCapacity) {
          showError('容納人數下限不可大於上限');
          submitting.value = false;
          return;
        }

        const submitData = {
          ...form.value,
          storeId: storeId.value,
          capacity: maxCapacity,
          minCapacity,
          maxCapacity
        };
        await reservationService.updateTable(route.params.id, submitData);
        showSuccess('座位更新成功');
        backToList();
      } catch (err) {
        const errorData = err.response?.data;
        if (errorData?.error?.details) {
          errors.value = errorData.error.details;
        }
        showError(errorData?.message || '更新座位失敗');
      } finally {
        submitting.value = false;
      }
    };

    const handleCancel = () => {
      backToList();
    };

    onMounted(() => {
      fetchStores();
      fetchRoomCategories();
      fetchTable();
    });

    return {
      storeId,
      stores,
      storesLoading,
      form,
      submitting,
      errors,
      tableTypeOptions,
      roomCategories,
      handleSubmit,
      handleCancel
    };
  }
};
</script>

<style scoped>
.create-table-container {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-title {
  font-size: 24px;
  font-weight: 600;
  margin: 0;
}

.form-container {
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.form-section {
  margin-bottom: 32px;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 2px solid #e0e0e0;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group.full-width {
  grid-column: 1 / -1;
}

.form-group label {
  margin-bottom: 6px;
  font-weight: 500;
}

.form-group label.required::after {
  content: ' *';
  color: #dc3545;
}

.form-hint {
  margin-top: 6px;
  color: #6b7280;
  font-size: 13px;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e0e0e0;
}

@media (max-width: 768px) {
  .form-row {
    grid-template-columns: 1fr;
  }
}
</style>
